/**
 * MiniBarList — labelled horizontal bars for "top N" / breakdown data.
 *
 * Bar widths are relative to the largest value in the set so the leading row
 * always fills the track. Pure CSS, no charting dependency.
 */

import React from 'react';
import {AppSmallText} from '../../../../Presentation/components/ui/Typography';
import type {AnalyticsTone} from '../models/homeAnalytics.model';
import {toneStyle} from '../utils/toneStyles';

export interface MiniBarRow {
  label: string;
  value: number;
  subtitle?: string;
}

interface MiniBarListProps {
  rows: MiniBarRow[];
  tone?: AnalyticsTone;
  emptyLabel: string;
  /** Show a 1-based rank chip before each label (for "top stores/visitors"). */
  ranked?: boolean;
}

const MiniBarList: React.FC<MiniBarListProps> = ({
  rows,
  tone = 'primary',
  emptyLabel,
  ranked = false
}) => {
  if (rows.length === 0) {
    return <AppSmallText className="text-gray-400">{emptyLabel}</AppSmallText>;
  }

  const max = Math.max(...rows.map((row) => row.value), 1);
  const styles = toneStyle(tone);

  return (
    <ul className="space-y-3">
      {rows.map((row, index) => (
        <li key={`${row.label}-${index}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              {ranked && (
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full ${styles.softBg} ${styles.text} text-xs font-semibold flex items-center justify-center`}
                >
                  {index + 1}
                </span>
              )}
              <span className="truncate text-sm text-grayText">
                {row.label}
              </span>
              {row.subtitle && (
                <span className="truncate text-xs text-gray-400">
                  · {row.subtitle}
                </span>
              )}
            </div>
            <span className="flex-shrink-0 text-sm font-semibold text-grayText">
              {row.value.toLocaleString('en-US')}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${styles.bg} transition-all duration-500`}
              style={{width: `${Math.max((row.value / max) * 100, 2)}%`}}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

export default MiniBarList;
